import React from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faKeyboard,
  faUser,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import CountTextField from "../../CountTextField";
import {
  COLORS,
  FONT,
  inputBaseSx,
  selectSx,
  menuItemSx,
  menuPaperStyle,
} from "../constants";

// ─── Sub-components ──────────────────────────────────────────────────────────

function CrawlTypeToggle({ crawlType, onChange }) {
  const options = [
    { key: "keyword", label: "By Keyword", icon: faKeyboard },
    { key: "user", label: "By User", icon: faUser },
  ];

  return (
    <div
      style={{
        display: "inline-flex",
        backgroundColor: COLORS.surface,
        borderRadius: "12px",
        padding: "4px",
        border: `1px solid ${COLORS.border}`,
        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      {options.map(({ key, label, icon }) => {
        const active = crawlType === key;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              border: "none",
              outline: "none",
              cursor: "pointer",
              fontFamily: FONT,
              fontWeight: active ? 600 : 450,
              fontSize: "13px",
              borderRadius: "9px",
              padding: "8px 22px",
              minWidth: "125px",
              justifyContent: "center",
              color: active ? "#fff" : COLORS.textMuted,
              background: active
                ? `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`
                : "transparent",
              boxShadow: active
                ? "0 2px 8px rgba(79,70,229,0.3)"
                : "none",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <FontAwesomeIcon
              icon={icon}
              style={{ fontSize: "12px", opacity: active ? 1 : 0.55 }}
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function HelpTooltip({ crawlType }) {
  const content =
    crawlType === "keyword" ? (
      <div style={{ padding: "4px 0" }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "13px" }}>
          Keyword Crawl
        </div>
        <ul style={{ paddingLeft: 16, margin: 0, lineHeight: 1.7 }}>
          <li>Enter keyword(s) — comma-separated</li>
          <li>Pick a platform to search on</li>
          <li>Max 500 posts per request</li>
          <li>Sort available for Twitter &amp; YouTube</li>
        </ul>
      </div>
    ) : (
      <div style={{ padding: "4px 0" }}>
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "13px" }}>
          User Profile Crawl
        </div>
        <ul style={{ paddingLeft: 16, margin: 0, lineHeight: 1.7 }}>
          <li>Enter a username or full profile URL</li>
          <li>Select the matching platform</li>
          <li>Max 500 posts per request</li>
        </ul>
      </div>
    );

  return (
    <Tooltip
      title={content}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: "12px",
            backgroundColor: COLORS.surface,
            color: COLORS.text,
            boxShadow: COLORS.shadowLg,
            border: `1px solid ${COLORS.border}`,
            padding: "10px 14px",
            borderRadius: "10px",
            fontFamily: FONT,
            maxWidth: 270,
          },
        },
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          cursor: "pointer",
          padding: "6px 10px",
          borderRadius: "8px",
        }}
      >
        <FontAwesomeIcon
          icon={faCircleInfo}
          style={{ fontSize: 14, color: COLORS.primaryMid }}
        />
        <span style={{ fontFamily: FONT, fontSize: "12px", color: COLORS.textMuted }}>
          Help
        </span>
      </div>
    </Tooltip>
  );
}

// ─── Main CrawlForm ──────────────────────────────────────────────────────────

export default function CrawlForm({
  // Crawl-type state
  crawlType,
  onCrawlTypeChange,
  // Inputs
  keywordInput,
  onKeywordChange,
  userProfileInput,
  onUserProfileChange,
  // Platform
  selectedPlatform,
  onPlatformChange,
  availablePlatforms,
  // Sort
  sortOption,
  onSortChange,
  currentSortOptions,
  // Post count
  postCount,
  setPostCount,
  // Objective
  crawlOutputType,
  onOutputTypeChange,
  // Submit
  isFormValid,
  isSubmitting,
  onSubmit,
}) {
  return (
    <div
      className="c-search-form-container c-content-width"
      style={{
        background: COLORS.surface,
        borderRadius: "16px",
        border: `1px solid ${COLORS.border}`,
        boxShadow: COLORS.shadowMd,
        padding: 0,
        overflow: "hidden",
        animation: "formReveal 0.3s ease-out forwards",
      }}
    >
      {/* Row 1 — crawl-type toggle + help */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px 12px",
          borderBottom: `1px solid ${COLORS.border}`,
          background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, ${COLORS.surface} 100%)`,
        }}
      >
        <CrawlTypeToggle crawlType={crawlType} onChange={onCrawlTypeChange} />
        <HelpTooltip crawlType={crawlType} />
      </div>

      {/* Row 2 — form fields */}
      <div
        className="c-search-form"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "20px",
          flexWrap: "wrap",
        }}
      >
        {/* Platform */}
        <div className="c-platform-select-container" style={{ minWidth: 160 }}>
          <TextField
            onClick={(e) => e.stopPropagation()}
            className="c-platform-select"
            select
            label="Platform"
            value={selectedPlatform}
            onChange={onPlatformChange}
            margin="none"
            SelectProps={{
              MenuProps: { PaperProps: { style: menuPaperStyle } },
            }}
            InputProps={{ style: { height: "44px" } }}
            sx={selectSx}
          >
            {availablePlatforms.map((p) => (
              <MenuItem key={p} value={p} sx={menuItemSx}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Main input — keyword or user profile */}
        {crawlType === "keyword" ? (
          <TextField
            className="c-keyword-input"
            label="Search Keywords"
            value={keywordInput}
            onChange={(e) => onKeywordChange(e.target.value)}
            fullWidth
            margin="none"
            placeholder="e.g. AI, Climate Change, Elections"
            InputProps={{
              startAdornment: (
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  style={{ color: COLORS.textLight, fontSize: 13, marginRight: 8 }}
                />
              ),
            }}
            sx={inputBaseSx}
          />
        ) : (
          <TextField
            className="c-keyword-input"
            label="Username / Profile Link"
            value={userProfileInput}
            onChange={(e) => onUserProfileChange(e.target.value)}
            fullWidth
            margin="none"
            placeholder="e.g. @username or https://twitter.com/username"
            InputProps={{
              startAdornment: (
                <FontAwesomeIcon
                  icon={faUser}
                  style={{ color: COLORS.textLight, fontSize: 12, marginRight: 8 }}
                />
              ),
            }}
            sx={inputBaseSx}
          />
        )}

        {/* Sort (conditional) */}
        {crawlType === "keyword" && currentSortOptions.length > 0 && (
          <FormControl sx={{ minWidth: 135 }} size="small">
            <InputLabel
              id="sort-option-label"
              sx={{
                fontFamily: FONT,
                fontSize: "13px",
                color: COLORS.textMuted,
                "&.Mui-focused": { color: COLORS.primary },
              }}
            >
              Sort By
            </InputLabel>
            <Select
              labelId="sort-option-label"
              value={sortOption}
              onChange={(e) => onSortChange(e.target.value)}
              label="Sort By"
              sx={{
                height: "44px",
                borderRadius: "10px",
                fontFamily: FONT,
                fontSize: "13px",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.primaryMid,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.borderFocus,
                  borderWidth: "1.5px",
                },
              }}
              MenuProps={{ PaperProps: { style: menuPaperStyle } }}
            >
              {currentSortOptions.map((opt) => (
                <MenuItem key={opt} value={opt} sx={menuItemSx}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <CountTextField postCount={postCount} setPostCount={setPostCount} />

        {/* Objective */}
        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel
            id="type-option-label"
            sx={{
              fontFamily: FONT,
              fontSize: "13px",
              color: COLORS.textMuted,
              "&.Mui-focused": { color: COLORS.primary },
            }}
          >
            Objective
          </InputLabel>
          <Select
            labelId="type-option-label"
            value={crawlOutputType}
            onChange={(e) => onOutputTypeChange(e.target.value)}
            label="Objective"
            sx={{
              height: "44px",
              borderRadius: "10px",
              fontFamily: FONT,
              fontSize: "13px",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.primaryMid,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.borderFocus,
                borderWidth: "1.5px",
              },
            }}
            MenuProps={{ PaperProps: { style: menuPaperStyle } }}
          >
            {["Section Analysis", "Sentiment Analysis"].map((opt) => (
              <MenuItem key={opt} value={opt} sx={menuItemSx}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Submit */}
        <Button
          className="c-submit-button"
          variant="contained"
          disabled={!isFormValid || isSubmitting}
          onClick={onSubmit}
          sx={{
            height: "44px",
            minWidth: "120px",
            padding: "0 20px",
            borderRadius: "10px",
            fontFamily: FONT,
            fontSize: "13px",
            fontWeight: 600,
            letterSpacing: "0.3px",
            textTransform: "none",
            gap: "8px",
            background: isFormValid
              ? `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`
              : "transparent",
            border: isFormValid ? "none" : `1.5px solid ${COLORS.border}`,
            color: isFormValid ? "#fff" : COLORS.textMuted,
            boxShadow: isFormValid
              ? "0 4px 14px rgba(79,70,229,0.25)"
              : "none",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": isFormValid
              ? {
                  background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, #312E81 100%)`,
                  boxShadow: "0 6px 20px rgba(79,70,229,0.35)",
                  transform: "translateY(-1px)",
                }
              : {},
            "&:active": { transform: "translateY(0)", boxShadow: "none" },
            "&.Mui-disabled": {
              background: "transparent",
              border: `1.5px solid ${COLORS.border}`,
              color: COLORS.textMuted,
              boxShadow: "none",
            },
          }}
        >
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            style={{
              fontSize: "13px",
              color: isFormValid ? "#fff" : COLORS.textMuted,
              transition: "color 0.2s ease",
            }}
          />
          Run Crawl
        </Button>
      </div>
    </div>
  );
}
